/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent, useCallback, SetStateAction } from 'react'
import { FlatList, FlatListProps, ListRenderItemInfo } from 'react-native'
import styled, { DefaultTheme } from 'styled-components/native'

import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { getComputedAccessibilityLabel } from 'shared/accessibility/helpers/getComputedAccessibilityLabel'
import { RadioButton } from 'ui/designSystem/RadioButton/RadioButton'
import { Variant } from 'ui/designSystem/RadioButton/types'
import { RadioButtonGroupOption } from 'ui/designSystem/RadioButtonGroup/types'
import { SelectableSizing } from 'ui/designSystem/types'
import { ErrorFilled } from 'ui/svg/icons/ErrorFilled'
import { Typo } from 'ui/theme'

import { LabelVariant, renderRadioGroupLabel } from './labelUtils'

type RadioButtonGroupDisplay = 'horizontal' | 'vertical'

type RadioButtonGroupFlatListProps = {
  onEndReached?: () => void
  refreshing?: boolean
  onRefresh?: () => void
  onScroll?: FlatListProps<RadioButtonGroupOption>['onScroll']
  ListFooterComponent?: React.ReactElement
}

type Props = {
  label: string
  description?: string
  labelVariant?: LabelVariant
  options: Array<RadioButtonGroupOption>
  errorText?: string
  error?: boolean
  variant?: Variant
  disabled?: boolean
  display?: RadioButtonGroupDisplay
  value: string
  onChange: (value: string) => void
  flatListProps?: RadioButtonGroupFlatListProps
}

const generateRadioAccessibilityLabel = (
  groupLabel: string,
  item: RadioButtonGroupOption,
  index: number,
  total: number,
  isSelected: boolean
): string => {
  const positionLabel = `Élément ${index + 1} sur ${total}`
  const selectionLabel = isSelected ? 'sélectionné' : 'non sélectionné'
  return getComputedAccessibilityLabel(
    groupLabel,
    'Liste',
    positionLabel,
    item.label,
    item.description,
    selectionLabel
  )
}

export const RadioButtonGroup: FunctionComponent<Props> = ({
  label,
  description,
  options,
  errorText,
  error = false,
  variant = 'default',
  disabled = false,
  display = 'vertical',
  labelVariant = 'body',
  value,
  onChange,
  flatListProps,
}: Props) => {
  const keyExtractor = useCallback((item: RadioButtonGroupOption) => item.key, [])
  const totalOptions = options.length

  const handleSetValue = useCallback(
    (next: SetStateAction<string>) => {
      const resolved = typeof next === 'function' ? next(value) : next
      if (disabled || resolved === value) return
      onChange(resolved)
    },
    [disabled, value, onChange]
  )

  const renderRadioButton = useCallback(
    (item: RadioButtonGroupOption, index: number) => {
      const isSelected = value === item.label
      const isOptionDisabled = item.disabled ?? disabled
      return (
        <RadioButton
          key={item.key}
          label={item.label}
          disabled={isOptionDisabled}
          error={error}
          value={value}
          setValue={isOptionDisabled || isSelected ? () => undefined : handleSetValue}
          variant={variant}
          description={item.description}
          collapsed={item.collapsed}
          asset={item.asset}
          sizing={computedRadioButtonSizing(item.sizing, display)}
          accessibilityLabel={generateRadioAccessibilityLabel(
            label,
            item,
            index,
            totalOptions,
            isSelected
          )}
        />
      )
    },
    [disabled, error, value, handleSetValue, variant, display, label, totalOptions]
  )

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<RadioButtonGroupOption>) => renderRadioButton(item, index),
    [renderRadioButton]
  )

  const ItemSeparator = useCallback(
    () => <ItemSeparatorView variant={variant} display={display} />,
    [variant, display]
  )

  const renderOptionsContent = () => {
    if (flatListProps) {
      return (
        <StyledFlatList
          data={options}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          horizontal={display === 'horizontal'}
          onEndReached={flatListProps.onEndReached}
          refreshing={flatListProps.refreshing}
          onRefresh={flatListProps.onRefresh}
          onScroll={flatListProps.onScroll}
          ListFooterComponent={flatListProps.ListFooterComponent}
          ItemSeparatorComponent={ItemSeparator}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        />
      )
    }

    return (
      <OptionsContainer display={display} variant={variant}>
        {options.map((item, index) => renderRadioButton(item, index))}
      </OptionsContainer>
    )
  }

  return (
    <RadioButtonGroupContainer
      accessibilityRole={AccessibilityRole.GROUP}
      useFlatList={!!flatListProps}>
      <TitleContainer>
        {renderRadioGroupLabel(label, labelVariant)}
        {renderDescription(description, labelVariant)}
        {error && errorText ? (
          <ErrorContainer>
            <ErrorIcon />
            <ErrorText>{errorText}</ErrorText>
          </ErrorContainer>
        ) : null}
      </TitleContainer>
      {renderOptionsContent()}
    </RadioButtonGroupContainer>
  )
}

const computedRadioButtonSizing = (
  sizing: SelectableSizing | undefined,
  display: RadioButtonGroupDisplay
) => {
  if (sizing) return sizing
  return display === 'horizontal' ? 'hug' : 'fill'
}

const RadioButtonGroupContainer = styled.View<{ useFlatList: boolean }>(
  ({ theme, useFlatList }) => ({
    flex: useFlatList ? 1 : undefined,
    gap: theme.designSystem.size.spacing.s,
  })
)

const StyledFlatList = styled(FlatList)<FlatListProps<RadioButtonGroupOption>>({
  flex: 1,
  overflow: 'scroll',
})

const OptionsContainer = styled.View<{ variant: Variant; display: RadioButtonGroupDisplay }>(
  ({ theme, variant, display }) => ({
    flexWrap: display === 'horizontal' ? 'wrap' : 'nowrap',
    flexDirection: display === 'vertical' ? 'column' : 'row',
    gap: computeGap({ variant, display, theme }),
  })
)

const ItemSeparatorView = styled.View<{
  variant: Variant
  display: RadioButtonGroupDisplay
}>(({ theme, variant, display }) => ({
  height: display === 'vertical' ? computeGap({ variant, display, theme }) : undefined,
  width: display === 'horizontal' ? computeGap({ variant, display, theme }) : undefined,
}))

const ErrorContainer = styled.View(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.designSystem.size.spacing.xs,
}))

const TitleContainer = styled.View(({ theme }) => ({
  gap: theme.designSystem.size.spacing.xs,
}))

const DescriptionBody = styled(Typo.Body)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
}))

const DescriptionBodyS = styled(Typo.BodyS)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
}))

const renderDescription = (description?: string, labelVariant?: LabelVariant) => {
  if (!description) return null

  const isTitleVariant = labelVariant === 'title2' || labelVariant === 'title3'
  const DescriptionComponent = isTitleVariant ? DescriptionBody : DescriptionBodyS

  return <DescriptionComponent>{description}</DescriptionComponent>
}

const computeGap = ({
  variant,
  display,
  theme,
}: {
  variant: Variant
  display: RadioButtonGroupDisplay
  theme: DefaultTheme
}) => {
  if (display === 'horizontal') return theme.designSystem.size.spacing.l
  if (variant === 'default') return theme.designSystem.size.spacing.xl
  return theme.designSystem.size.spacing.s
}

const ErrorText = styled(Typo.BodyXs)(({ theme }) => ({
  color: theme.designSystem.color.text.error,
}))

const ErrorIcon = styled(ErrorFilled).attrs(({ theme }) => ({
  size: theme.designSystem.size.icon.s,
  color: theme.designSystem.color.icon.error,
}))``
